import { IApplicationCustomVariablesValues } from 'app/interfaces/customVariablesInterfaces';
import { BaseHttpService } from 'app/services';
import { TSubscriber } from 'modules/products/pages/ApplicationRequest/SubscribersStep/SubscribersAddNewSideSheet';
import { TCustodianRequest } from 'modules/products/interfaces/custodian';
import { SignatureProvider } from 'modules/products/pages/interfaces';
import { BilletResult } from 'modules/products/interfaces/billets';
import { ValidateEmvResponse } from 'modules/products/interfaces/qrCode';
import {
  IBankAccount,
  IPixKey,
} from 'modules/customer/context/CustomerProvider/customer.interfaces';
import { ISimulationResponse } from 'modules/products/pages/ApplicationRequest/SimulationV2Step/CreditSimulatorV2/types';
import { ConsigneeAuthorizationPayload } from 'modules/products/pages/ApplicationRequest/ConsignedStep/interfaces';
import { TApplicationGuaranteeInfoPayload } from 'modules/products/pages/ApplicationRequest/ConsultaFgts/interfaces';
import { TApplicationBeneficiaryAccount } from '../hooks/useApplicationsService';

export enum SignatureAuthenticationMode {
  NONE = 'NONE',
  PIX = 'PIX',
  FACIAL_BIOMETRICS = 'FACIAL_BIOMETRICS',
  BIOMETRIC = 'BIOMETRIC',
  EMAIL = 'EMAIL',
  LIVENESS = 'LIVENESS',
  SELFIE = 'SELFIE',
  SMS = 'SMS',
  WHATSAPP = 'WHATSAPP',
  ADDRESS_PROOF = 'ADDRESS_PROOF',
  ICP_BRASIL = 'ICP_BRASIL',
  HANDWRITTEN = 'HANDWRITTEN',
  OFFICIAL_DOCUMENT = 'OFFICIAL_DOCUMENT',
}

export enum SignatureUnicoAuthenticationMode {
  DOC_SIGN = 'DOC_SIGN',
  DOC_SIGN_SERPRO = 'DOC_SIGN_SERPRO',
}

export type TApplicationSplitBeneficiaryAccount = {
  external_bank_account?: IBankAccount;
  holder: {
    name: string;
    taxpayer_id: string;
  };
  pix?: IPixKey;
  percentage?: number;
  amount?: number;
};

export type TApplicationParametersDataRequest = {
  amount: number;
  numPayments: number;
  tacAmount?: number;
  tacRate?: number;
  interestRate: number;
  financeFee: number;
  insurance_amount?: number;
  firstPaymentDate: string;
  disbursementDate: string;
  borrowerId: string;
  productId: string;
  beneficiaryAccountId?: string;
  fundingId: string;
  customVariables: IApplicationCustomVariablesValues[] | null;
  subscribers: TSubscriber[] | null;
  signatureType: string;
  custodian: TCustodianRequest | null;
  signatureProvider: SignatureProvider;
  paymentMethod?: 'PIX' | 'TED';
  signatureAuthenticationOptions?: {
    mode?: SignatureAuthenticationMode | SignatureUnicoAuthenticationMode;
  };
  split_beneficiary_account?: TApplicationSplitBeneficiaryAccount;
  signatureCollectOptions?: {
    requireSelfPhoto?: boolean;
    requireSelfieValidation?: boolean;
  };
  billets_info?: Array<BilletResult> | null;
  qrcode_info?: ValidateEmvResponse | null;
  additional_installment_fee?: number | null;
  additional_installment_description?: string | null;
  balance_check_id?: string;
  financialIndicatorCobransaas?: ISimulationResponse;
  gracePeriodAmortization?: number[];
  amortizationType?: string;
  amortizationFrequency?: string;
  interestPreType?: string;
  interestRateFrequency?: string;
  customCcbTemplateId?: string;
  simulation_id?: string | null;
  guarantee_info?: TApplicationGuaranteeInfoPayload;
};

interface IConsigneeAuthorizationPayload {
  taxpayer_id: string;
  product: { id: string };
}

export class ApplicationsHttpService extends BaseHttpService {
  BASE_URL = `${process.env.REACT_APP_DASHBOARD_API_HOST}/proxy`;
  createApplication = async (
    applicationParametersData: TApplicationParametersDataRequest,
  ) => {
    const { subscribers } = applicationParametersData;
    const parsedCustomVariables = {};
    applicationParametersData?.customVariables
      ? applicationParametersData?.customVariables.map((cVariable) => {
          if (
            cVariable.value?.trim?.() !== '' &&
            cVariable.value !== null &&
            cVariable.value !== undefined
          ) {
            Object.assign(parsedCustomVariables, {
              [cVariable.name || '']: cVariable.value,
            });
          }
        })
      : [];

    const coSigners = subscribers
      ?.filter((subscriber) => subscriber.subscriberType === 'co_signers')
      .map((subscriber) => ({
        co_signer_id: subscriber.subscriber.id,
        type: subscriber.subscriberPersonType,
      }));

    const guarantors = subscribers
      ?.filter((subscriber) => subscriber.subscriberType === 'guarantors')
      .map((subscriber) => ({
        guarantor_id: subscriber.subscriber.id,
        type: subscriber.subscriberPersonType,
      }));

    const investors = subscribers
      ?.filter((subscriber) => subscriber.subscriberType === 'investors')
      .map((subscriber) => ({
        investor_id: subscriber.subscriber.id,
        type: subscriber.subscriberPersonType,
      }));

    const coDebtor = subscribers
      ?.filter((subscriber) => subscriber.subscriberType === 'co_debtors')
      .map((subscriber) => ({
        co_debtor_id: subscriber.subscriber.id,
        type: subscriber.subscriberPersonType,
      }));

    const representative = subscribers
      ?.filter((subscriber) => subscriber.subscriberType === 'representatives')
      .map((subscriber) => ({
        representative_id: subscriber.subscriber.id,
        type: subscriber.subscriberPersonType,
      }));

    const surety = subscribers
      ?.filter((subscriber) => subscriber.subscriberType === 'sureties')
      .map((subscriber) => ({
        surety_id: subscriber.subscriber.id,
        type: subscriber.subscriberPersonType,
      }));

    const collateralProvider = subscribers
      ?.filter(
        (subscriber) => subscriber.subscriberType === 'collateral_providers',
      )
      .map((subscriber) => ({
        collateral_provider_id: subscriber.subscriber.id,
        type: subscriber.subscriberPersonType,
      }));

    const res = await this.post(`${this.BASE_URL}/originator/applications`, {
      requested_amount: applicationParametersData.amount,
      interest_rate: applicationParametersData.interestRate,
      tac_amount: applicationParametersData.tacAmount,
      tac_rate: applicationParametersData.tacRate,
      finance_fee: applicationParametersData.financeFee,
      insurance_amount: applicationParametersData.insurance_amount,
      num_payments: applicationParametersData.numPayments,
      first_payment_date: applicationParametersData.firstPaymentDate,
      disbursement_date: applicationParametersData.disbursementDate,
      amortization_type: applicationParametersData.amortizationType,
      amortization_frequency:
        applicationParametersData?.amortizationFrequency || null,
      grace_period_amortization:
        applicationParametersData.gracePeriodAmortization,
      interest_pre_type: applicationParametersData.interestPreType,
      interest_rate_frequency:
        applicationParametersData?.interestRateFrequency || null,
      financial_indicator_cobransaas:
        applicationParametersData.financialIndicatorCobransaas,
      borrower: {
        id: applicationParametersData.borrowerId,
      },
      product: {
        id: applicationParametersData.productId,
      },
      signature_collect_method:
        applicationParametersData.signatureType || 'LINK',
      co_signers: coSigners || undefined,
      guarantors: guarantors || undefined,
      investors: investors || undefined,
      co_debtors: coDebtor || undefined,
      collateral_providers: collateralProvider || undefined,
      representatives: representative || undefined,
      sureties: surety || undefined,
      custodian: applicationParametersData.custodian || undefined,
      signature_provider: applicationParametersData.signatureProvider,
      payment_method: applicationParametersData.paymentMethod,
      billets_info: applicationParametersData.billets_info,
      qrcode_info: applicationParametersData.qrcode_info,
      custom_ccb_template_id: applicationParametersData.customCcbTemplateId,
      split_beneficiary_account:
        applicationParametersData.split_beneficiary_account,
      additional_installment_description:
        applicationParametersData.additional_installment_description,
      additional_installment_fee:
        applicationParametersData.additional_installment_fee,
      beneficiary_account: applicationParametersData.beneficiaryAccountId
        ? {
            registered_account_id:
              applicationParametersData.beneficiaryAccountId,
          }
        : undefined,
      funding: {
        id: applicationParametersData.fundingId,
      },
      custom_variables: {
        values: {
          ...parsedCustomVariables,
        },
      },
      balance_check_id: applicationParametersData.balance_check_id,
      simulation_id: applicationParametersData?.simulation_id || undefined,
      ...(applicationParametersData.guarantee_info && {
        guarantee_info: applicationParametersData.guarantee_info,
      }),
      ...(applicationParametersData?.signatureAuthenticationOptions && {
        signature_authentication_options: {
          mode: applicationParametersData?.signatureAuthenticationOptions?.mode,
        },
      }),
      ...(applicationParametersData?.signatureCollectOptions && {
        signature_collect_options: {
          require_self_photo:
            applicationParametersData?.signatureCollectOptions
              ?.requireSelfPhoto,
          require_selfie_validation:
            applicationParametersData?.signatureCollectOptions
              ?.requireSelfieValidation,
        },
      }),
    });
    return res;
  };

  getApplication = async (applicationId: string, isIssuer?: boolean) => {
    const entity = isIssuer ? 'issuer' : 'originator';
    const res = await this.get(
      `${this.BASE_URL}/${entity}/applications/${applicationId}`,
    );
    return res;
  };

  getBasePremiumAmount = async (financedAmount: number, fundingId: string) => {
    const res = await this.post(
      `${this.BASE_URL}/originator/applications/preview-premium-amount`,
      {
        financed_amount: financedAmount,
        funding_id: fundingId,
      },
    );
    return res;
  };

  getFinancialIndicators = async () => {
    const res = await this.get(
      `${this.BASE_URL}/originator/applications/financial-indicators`,
    );
    return res;
  };

  getApplications = async (
    page: number,
    size?: number,
    parameters?: { [key: string]: string | null | undefined },
    filterStatusSelect?: string[],
    filterIds?: string[],
    isIssuer?: boolean,
  ) => {
    const filterStatusSelectParsed: string[] = [];
    const filterIdsParsed: string[] = [];

    filterStatusSelect &&
      filterStatusSelect.map((item) => {
        filterStatusSelectParsed.push(`&status=${item}`);
      });

    filterIds &&
      filterIds.map((item) => {
        filterIdsParsed.push(`&ids=${item}`);
      });

    const entity = isIssuer ? 'issuer' : 'originator';

    const res = await this.get(
      `${this.BASE_URL}/${entity}/applications?page=${page || 0}&size=${
        size || 10
      }&${
        parameters
          ? Object.entries(parameters)
              .filter((item) => item?.[1])
              .map((item) => {
                return `${item?.[0]}=${item?.[1]}`;
              })
              .join('&')
          : ''
      }${filterStatusSelectParsed
        .toString()
        .replaceAll(',', '')}${filterIdsParsed.toString().replaceAll(',', '')}`,
    );
    return res;
  };

  cancelApplication = async (applicationId: string) => {
    const res = await this.post(
      `${this.BASE_URL}/originator/applications/${applicationId}/cancel`,
      {},
    );
    return res;
  };

  resendSignature = async (applicationId: string) => {
    const res = await this.put(
      `${this.BASE_URL}/originator/applications/${applicationId}/resend-signature`,
      {},
    );
    return res;
  };

  sendConsigneeContract = async (applicationId: string) => {
    const res = await this.post(
      `${this.BASE_URL}/originator/applications/${applicationId}/send-consignee-contract`,
      {},
    );
    return res;
  };

  submitConsignee = async (applicationId: string) => {
    const res = await this.post(
      `${this.BASE_URL}/originator/applications/${applicationId}/submitted-consignee-contract`,
    );
    return res;
  };

  getApplicationStatusOptions = async (productId: string) => {
    const res = await this.get(
      `${this.BASE_URL}/issuer/products/${productId}/statuses-definitions`,
    );
    return res;
  };

  uploadCustomCcbTemplate = async (data: FormData) => {
    const res = await this.post(
      `${this.BASE_URL}/originator/applications/custom-ccb-template`,
      data,
    );
    return res;
  };

  getQualificationRequestApplication = async (
    productId: string,
    page: number,
    borrowerId?: string,
    applicationId?: string,
    role?: string,
  ) => {
    const res = await this.get(
      `${
        this.BASE_URL
      }/originator/products/${productId}/qualification-requests?role=${role}&page=${page}&size=10${
        typeof borrowerId === 'string' ? `&borrower_id=${borrowerId}` : ''
      }${
        typeof applicationId === 'string'
          ? `&application_id=${applicationId}`
          : ''
      }`,
    );
    return res.data;
  };

  getApplicationSignatures = async (applicationId: string) => {
    const res = await this.get(
      `${this.BASE_URL}/originator/applications/${applicationId}/signatures`,
    );
    return res.data;
  };

  getAgreementFile = async (applicationId: string) => {
    const res = await this.get(
      `${this.BASE_URL}/originator/applications/${applicationId}/agreement`,
    );
    return res.data;
  };

  getSignedAgreementFile = async (applicationId: string) => {
    const res = await this.get(
      `${this.BASE_URL}/originator/applications/${applicationId}/signed-agreement/presigned-url`,
    );
    return res.data;
  };

  getSignedEndorsementFile = async (applicationId: string) => {
    const res = await this.get(
      `${this.BASE_URL}/originator/applications/${applicationId}/signed-endorsement`,
    );
    return res.data;
  };

  getSignedSecuritizationEndorsementFile = async (applicationId: string) => {
    const res = await this.get(
      `${this.BASE_URL}/originator/applications/${applicationId}/signed-securitization-endorsement`,
    );
    return res.data;
  };

  getUnicoDocuments = async (applicationId: string) => {
    const res = await this.get(
      `${this.BASE_URL}/originator/applications/${applicationId}/attachments`,
    );
    return res.data;
  };

  getSignedMarginConsentFile = async (taxpayer_id: string) => {
    const res = await this.get(
      `${this.BASE_URL}/originator/guarantee/acceptterm/download?taxpayerId=${taxpayer_id}`,
    );
    return res.data;
  };

  updateApplicationPaymentOrder = async (
    applicationId: string,
    paymentOrderId: string,
    data: TApplicationBeneficiaryAccount,
  ) => {
    const res = await this.put(
      `${this.BASE_URL}/originator/applications/${applicationId}/payment-orders/${paymentOrderId}`,
      data,
    );
    return res.data;
  };

  createDisbursementAttempt = async (applicationId: string) => {
    const res = await this.post(
      `${this.BASE_URL}/originator/applications/${applicationId}/disbursement-attempts`,
      {},
    );
    return res.data;
  };

  commercialPaperStatusUpdate = async (applicationId: string) => {
    const res = await this.post(
      `${this.BASE_URL}/originator/applications/${applicationId}/commercial-paper-status-update`,
      {},
    );
    return res.data;
  };

  getConsignedAuthorization = async (
    payload: IConsigneeAuthorizationPayload,
  ) => {
    const res = await this.post(
      `${this.BASE_URL}/originator/guarantee/authorization`,
      payload,
    );
    return res;
  };

  getConsigned = async (
    taxId: string,
    productId: string,
    benefitNumber?: string,
  ) => {
    const params = new URLSearchParams({ taxpayer_id: taxId });

    if (benefitNumber) {
      params.append('benefit_number', benefitNumber);
    }

    const res = await this.get(
      `${this.BASE_URL}/originator/guarantee/${productId}/get-balance?${params.toString()}`,
    );

    return res;
  };

  getConsignedSync = async (id: string) => {
    const res = await this.get(
      `${this.BASE_URL}/originator/guarantee/get-balance-sync?id=${id}`,
    );

    return res;
  };

  getFgtsGuaranteeBalance = async (
    balanceCheckId: string,
    productId: string,
  ) => {
    const res = await this.get(
      `${this.BASE_URL}/originator/guarantee/${productId}/get-balance-fgts?balance_check_id=${balanceCheckId}`,
    );

    return res;
  };

  getFgtsGuaranteeBalanceSync = async (id: string) => {
    const res = await this.get(
      `${this.BASE_URL}/originator/guarantee/get-balance-fgts-sync?id=${id}`,
    );

    return res;
  };

  getConsignedStatus = async (taxId: string) => {
    const res = await this.get(
      `${this.BASE_URL}/originator/guarantee/authorization-status/${taxId}`,
    );

    return res.data;
  };

  sendConsigneeTermAuthorization = async (
    payload: ConsigneeAuthorizationPayload,
  ) => {
    const res = await this.post(
      `${this.BASE_URL}/originator/guarantee/authorization-permission`,
      payload,
    );

    return res.data;
  };

  getConsigneeReceipt = async (applicationId: string) => {
    const res = await this.get(
      `${this.BASE_URL}/originator/applications/receipt-consignee/download?id=${applicationId}`,
    );
    return res.data;
  };

  getApplicationGuarantee = async (applicationId: string) => {
    const res = await this.get(
      `${this.BASE_URL}/originator/applications/${applicationId}/guarantee`,
    );
    return res;
  };
}
